<script>
import { getHandler, getPoints, getSize, heightMap, pointMap, pointMap2, rad2deg, tr2bl, widthMap } from './helper'

/**
 * 控件變換框組件
 *
 * 提供控件的拖動、縮放、旋轉功能
 * 支持選中狀態顯示、邊界約束、網格對齊等功能
 */
export default {
  name: 'CellTransformBox',
  props: {
    disabled: { type: Boolean, default: true }, // 是否禁用控件
    inputType: { type: String, default: undefined }, // 輸入框類型
    value: {
      // 控件的位置和尺寸
      default: function () {
        return { x: 0, y: 0, width: 100, height: 100, rotation: 0 }
      },
      type: Object
    },
    handlerSize: {
      // 控制點尺寸
      type: Number,
      default: 11
    },
    active: {
      // 是否處於選中狀態
      default: true,
      type: Boolean
    },
    resizeHandler: {
      // 調整大小的控制點位置
      default: function () {
        return ['tl', 'tm', 'tr', 'r', 'br', 'bm', 'bl', 'l']
      },
      type: Array
    },
    resizable: {
      // 是否可調整大小
      default: true,
      type: Boolean
    },
    rotatable: {
      // 是否可旋轉
      default: true,
      type: Boolean
    },
    draggable: {
      // 是否可拖動
      default: true,
      type: Boolean
    },
    acceptRatio: {
      // 是否維持比例
      default: false,
      type: Boolean
    },
    minWidth: {
      // 最小寬度
      type: Number,
      default: 1
    },
    minHeight: {
      // 最小高度
      type: Number,
      default: 1
    },
    maxWidth: {
      // 最大寬度
      type: Number,
      default: 100000000
    },
    maxHeight: {
      // 最大高度
      type: Number,
      default: 100000000
    },
    parent: {
      // 是否限制在父元素內
      type: Boolean,
      default: false
    },
    beforeActive: {
      // 選中前的回調
      type: Function,
      default: () => false
    },
    id: {
      type: [Number, String],
      default: undefined
    },
    renderContent: {
      type: Function,
      default: undefined
    },
    grid: {
      // 網格大小
      type: Array,
      default: () => [1, 1]
    },
    stop: {
      // 是否阻止事件傳遞
      default: true,
      type: Boolean
    },
    prevent: {
      // 是否阻止默認事件
      default: true,
      type: Boolean
    },
    zoom: {
      // 縮放比例
      default: 1,
      type: Number
    },
    axis: {
      // 移動軸向
      type: String,
      default: 'xy'
    }
  },
  data() {
    return {
      transform: Object.assign({}, this.value),
      currentRatio: 1,
      isInitialRatio: false,
      isDragging: false,
      isResizing: false,
      isRotating: false,
      isReadyToDrag: false,
      isReadyToResize: false,
      isReadyToRotate: false,

      draggable1: true,
      rotatable1: true,
      resizable1: true
    }
  },
  computed: {
    /**
     * 計算旋轉控制點的樣式
     */
    rotateHandler() {
      const size = Math.ceil(this.handlerSize / this.zoom) + 'px'
      return `width:${size};height:${size};top:-25px;margin-left:${-Math.floor(this.handlerSize / 2)}px`
    },
    /**
     * 計算元件的樣式
     */
    style() {
      const transform = this.transform
      return `left:${transform.x}px;top:${transform.y}px;width:${transform.width}px;height:${transform.height}px;transform:rotate(${transform.rotation}deg)`
    }
  },
  watch: {
    value(t) {
      this.transform = { ...t }
    }
  },
  created() {
    this.localeTransform = { ...this.transform }
    this.draggable1 = this.draggable
    this.rotatable1 = this.rotatable
    this.resizable1 = this.resizable
    // 檢查是否為檢視模式，檢視模式下禁用所有變換功能
    if (window.localStorage.getItem('isViewMode') === 'true') {
      this.draggable1 = false
      this.rotatable1 = false
      this.resizable1 = false
    }
  },
  methods: {
    /**
     * 處理默認事件
     * @param {Event} e 事件對象
     */
    handleDefaultEvent(e) {
      if (this.stop) {
        e.stopPropagation()
      }
      if (this.prevent) {
        e.preventDefault()
      }
    },

    /**
     * 取得調整大小控制點的樣式
     * @param {string} type 控制點類型
     * @returns {string} 控制點樣式
     */
    getNewHandler(type) {
      const cursor = getHandler(type, this.transform.rotation)
      const { handlerSize } = this
      let props = {}
      const half = -Math.floor(handlerSize / 2) + 'px'
      switch (type) {
        case 'tl':
          props = { top: half, left: half }
          break
        case 'tm':
          props = { top: half, 'margin-left': half }
          break
        case 'tr':
          props = { right: half, top: half }
          break
        case 'r':
          props = { right: half, 'margin-top': half }
          break
        case 'br':
          props = { bottom: half, right: half }
          break
        case 'bm':
          props = { 'margin-left': half, bottom: half }
          break
        case 'bl':
          props = { left: half, bottom: half }
          break
        case 'l':
          props = { 'margin-top': half, left: half }
          break
      }
      const result = {
        cursor: cursor + '-resize',
        width: Math.ceil(handlerSize / this.zoom) + 'px',
        height: Math.ceil(handlerSize / this.zoom) + 'px',
        ...props
      }
      return Object.keys(result)
        .map((item) => `${item}:${result[item]}`)
        .join(';')
    },

    /**
     * 發出值變化事件
     */
    emitChange() {
      this.$emit('input', { ...this.transform })
    },

    /**
     * 處理滑鼠按下事件
     * @param {Event} event 事件對象
     */
    handleMouseDown(event) {
      if (this.disabled === false) {
        if (this.inputType === 'input' || this.inputType === 'date' || this.inputType === 'datetime') {
          this.$emit('dragstart', event, this.transform)
        }
        return
      }
      // 檢視模式下禁止選中控件
      if (window.localStorage.getItem('isViewMode') === 'true') {
        return
      }
      if (!this.active && !this.beforeActive(this.id)) return
      this.handleDefaultEvent(event)
      const point = event.touches ? event.touches[0] : event
      const { clientX, clientY } = point
      this._lastX = clientX
      this._lastY = clientY
      this._activeTarget = event.target
      this._parentRect = this.$refs.wrapper.parentNode.getBoundingClientRect()
      this._resizeHandler = event.target.dataset.resizetype
      this.localeTransform = { ...this.transform }

      // 計算邊界約束
      if (this.parent) {
        const minLeft = 0
        const maxLeft = this._parentRect.width / this.zoom - this.transform.width
        const minTop = 0
        const maxTop = this._parentRect.height / this.zoom - this.transform.height
        const maxLeftWidth = this.transform.x + this.transform.width
        const maxRightWidth = this._parentRect.width / this.zoom - this.transform.x
        const maxTopHeight = this.transform.y + this.transform.height
        const maxBottomHeight = this._parentRect.height / this.zoom - this.transform.y
        this.bounday = {
          minLeft,
          maxLeft,
          minTop,
          maxTop,
          maxLeftWidth,
          maxRightWidth,
          maxTopHeight,
          maxBottomHeight
        }
      }

      // 綁定事件處理程序
      document.addEventListener('mousemove', this.handleMouseMove, false)
      document.addEventListener('touchmove', this.handleMouseMove, false)
      document.addEventListener('touchend', this.handleMouseUp, false)
      document.addEventListener('mouseup', this.handleMouseUp, false)

      // 根據操作類型處理
      if (event.target.dataset.type === 'rotate') {
        this._handlerType = 'rotate'
        this.isReadyToRotate = true
        this.handleRotateStart(event)
        this.$emit('rotatestart', event, this.transform)
      } else if (this._activeTarget.dataset.resizetype) {
        this._handlerType = 'resize'
        this.isReadyToResize = true
        this.handleResizeStart(event)
        this.$emit('resizestart', event, this.transform)
      } else {
        this._handlerType = 'drag'
        this.isReadyToDrag = true
        this.draggable1 && this.$emit('dragstart', event, this.transform)
      }
    },

    /**
     * 處理滑鼠移動事件
     * @param {Event} event 事件對象
     */
    handleMouseMove(event) {
      this.handleDefaultEvent(event)
      const { clientX, clientY } = event.touches ? event.touches[0] : event
      this.isReadyToDrag = false
      this.isReadyToResize = false
      this.isReadyToRotate = false
      this._deltaX = clientX - this._lastX
      this._deltaY = clientY - this._lastY
      this._lastX = clientX
      this._lastY = clientY

      if (this._handlerType === 'resize') {
        this.isResizing = true
        this.handleResizeMove(event)
        this.$emit('resize', event, this.transform)
      } else if (this._handlerType === 'drag' && this.draggable1) {
        this.isDragging = true
        this.doMove(event)
        this.$emit('drag', event, this.transform)
      } else if (this._handlerType === 'rotate') {
        this.isRotating = true
        this.handleRotateMove(event)
        this.$emit('rotate', event, this.transform)
      }
      this.emitChange()
    },

    /**
     * 執行移動操作
     * 根據網格對齊和邊界限制更新控件位置
     */
    doMove() {
      const [gridX, gridY] = this.grid
      const deltaX = this._deltaX / this.zoom
      const deltaY = this._deltaY / this.zoom
      const x = (this.localeTransform.x = Math.round(this.localeTransform.x + deltaX))
      const y = (this.localeTransform.y = Math.round(this.localeTransform.y + deltaY))

      // 網格對齊和邊界限制
      if (this.axis.includes('x')) {
        if (x > this.transform.x) {
          this.transform.x = this.restrictToLeftOfParent(x - (x % gridX))
        } else if (x < this.transform.x) {
          this.transform.x = this.restrictToLeftOfParent(x - ((x % gridX) - gridX))
        }
      }

      if (this.axis.includes('y')) {
        if (y > this.transform.y) {
          this.transform.y = this.restrictToTopOfParent(y - (y % gridY))
        } else if (y < this.transform.y) {
          this.transform.y = this.restrictToTopOfParent(y - ((y % gridY) - gridY))
        }
      }
    },

    /**
     * 限制左邊界
     * @param {number} x X座標
     * @returns {number} 限制後的X座標
     */
    restrictToLeftOfParent(x) {
      if (!this.parent) return x
      x = Math.max(this.bounday.minLeft, x)
      x = Math.min(this.bounday.maxLeft, x)
      return x
    },

    /**
     * 限制上邊界
     * @param {number} y Y座標
     * @returns {number} 限制後的Y座標
     */
    restrictToTopOfParent(y) {
      if (!this.parent) return y
      y = Math.max(this.bounday.minTop, y)
      y = Math.min(this.bounday.maxTop, y)
      return y
    },

    /**
     * 限制高度
     * @param {number} h 高度
     * @param {string} type 控制點類型
     * @returns {number} 限制後的高度
     */
    restrictHeight(h, type) {
      if (!this.parent || this.transform.rotation > 0) return h
      if (['bl', 'bm', 'br'].includes(type)) return Math.min(this.bounday.maxBottomHeight, h)
      if (['tl', 'tm', 'tr'].includes(type)) return Math.min(this.bounday.maxTopHeight, h)
      return h
    },

    /**
     * 限制寬度
     * @param {number} w 寬度
     * @param {string} type 控制點類型
     * @returns {number} 限制後的寬度
     */
    restrictWidth(w, type) {
      if (!this.parent || this.transform.rotation > 0) return w
      if (['tl', 'l', 'bl'].includes(type)) return Math.min(this.bounday.maxLeftWidth, w)
      if (['tr', 'r', 'br'].includes(type)) return Math.min(this.bounday.maxRightWidth, w)
      return w
    },

    /**
     * 處理滑鼠放開事件
     * @param {Event} event 事件對象
     */
    handleMouseUp(event) {
      this.handleDefaultEvent(event)
      document.removeEventListener('mousemove', this.handleMouseMove, false)
      document.removeEventListener('mouseup', this.handleMouseUp, false)
      document.removeEventListener('touchmove', this.handleMouseMove, false)
      document.removeEventListener('touchend', this.handleMouseUp, false)

      const ev = {
        drag: 'draggable',
        resize: 'resizable',
        rotate: 'rotatable'
      }

      this.isInitialRatio = this.isDragging = this.isResizing = this.isRotating = false
      this.isReadyToDrag = this.isReadyToResize = this.isReadyToRotate = false
      this[ev[this._handlerType]] && this.$emit(this._handlerType + 'end', event, this.transform)
    },

    /**
     * 處理開始調整大小
     * @param {Event} event 事件對象
     */
    handleResizeStart(event) {
      const type = this._resizeHandler
      const rect = this.transform
      const matrix = getPoints(rect, this.zoom)
      let pressAngle
      const opposite = matrix[pointMap[type]]
      const opp2 = matrix[pointMap2[type]]
      const { clientX, clientY } = event.touches ? event.touches[0] : event
      const x1 = clientX - this._parentRect.left - opp2.x
      const y1 = clientY - this._parentRect.top - opp2.y
      const width = rect.width
      const height = rect.height

      if (tr2bl[type]) {
        pressAngle = rad2deg(Math.atan2(width, widthMap[type] ? height / 2 : height))
      } else {
        pressAngle = rad2deg(Math.atan2(height, heightMap[type] ? width / 2 : width))
      }
      const startAngle = rad2deg(Math.atan2(y1, x1))
      this._resizeOpt = {
        matrix,
        rect,
        type,
        opposite,
        opp2,
        pressAngle,
        startAngle
      }
    },

    /**
     * 處理調整大小移動
     * @param {Event} event 事件對象
     */
    handleResizeMove(event) {
      const { clientX, clientY } = event.touches ? event.touches[0] : event
      const { opposite, opp2, type, pressAngle, startAngle } = this._resizeOpt
      const x = clientX - this._parentRect.left - opp2.x
      const y = clientY - this._parentRect.top - opp2.y
      const dis = Math.hypot(y, x)
      const ratio = event.shiftKey || this.acceptRatio
      const [gridX, gridY] = this.grid

      if (!this.isInitialRatio && ratio) {
        this.currentRatio = this.transform.width / this.transform.height
        this.isInitialRatio = true
      }
      if (!ratio) {
        this.isInitialRatio = false
      }

      let { w, h } = getSize({
        type,
        dis,
        x,
        y,
        startAngle,
        pressAngle
      })
      const transform = { ...this.localeTransform }

      // 等比例縮放
      if (this.isInitialRatio) {
        if (widthMap[type]) h = w / this.currentRatio
        else w = h * this.currentRatio
      }

      // 轉換實際尺寸
      w /= this.zoom
      h /= this.zoom
      w = Math.min(Math.max(Math.round(w), this.minWidth), this.maxWidth)
      h = Math.min(Math.max(Math.round(h), this.minHeight), this.maxHeight)

      // 執行調整大小
      if (widthMap[type] && !ratio) {
        transform.width = w
      } else if (heightMap[type] && !ratio) {
        transform.height = h
      } else {
        transform.width = w
        transform.height = h
      }

      const resizeType = this._resizeHandler
      // 網格對齊
      if (transform.width % gridX > 0) {
        if (transform.width > this.localeTransform.width) {
          transform.width = transform.width - (transform.width % gridX)
        } else {
          transform.width = transform.width - ((transform.width % gridX) - gridX)
        }
      }

      if (transform.height % gridY > 0) {
        if (transform.height > this.localeTransform.height) {
          transform.height = transform.height - (transform.height % gridY)
        } else {
          transform.height = transform.height - ((transform.height % gridY) - gridY)
        }
      }

      // 邊界限制
      transform.height = this.restrictHeight(transform.height, resizeType)
      transform.width = this.restrictWidth(transform.width, resizeType)

      // 計算新位置
      const matrix = getPoints(transform, this.zoom)
      const newOpposite = matrix[pointMap[type]]
      const deltaX = -(newOpposite.x - opposite.x) / this.zoom
      const deltaY = -(newOpposite.y - opposite.y) / this.zoom
      transform.x = Math.round(transform.x + deltaX)
      transform.y = Math.round(transform.y + deltaY)
      this.transform = transform
    },

    /**
     * 處理開始旋轉
     * @param {Event} event 事件對象
     */
    handleRotateStart(event) {
      const { clientX, clientY } = event.touches ? event.touches[0] : event
      const t = this.$refs.wrapper.getBoundingClientRect()
      const cx = t.left + t.width / 2
      const cy = t.top + t.height / 2
      const startAngle = (180 / Math.PI) * Math.atan2(clientY - cy, clientX - cx)
      const rotation = this.transform.rotation
      this._rotateOpt = { cx, cy, startAngle, rotation }
    },

    /**
     * 處理旋轉移動
     * @param {Event} event 事件對象
     */
    handleRotateMove(event) {
      const { cx, cy, startAngle, rotation } = this._rotateOpt
      const { clientX, clientY } = event.touches ? event.touches[0] : event
      const x = clientX - cx
      const y = clientY - cy
      const angle = (180 / Math.PI) * Math.atan2(y, x)
      const currentAngle = angle - startAngle
      let r = rotation + currentAngle
      r = r % 360
      r = r < 0 ? r + 360 : r
      this.transform.rotation = Math.floor(r)
    },

    /**
     * 取得元件的所有樣式類別
     * @returns {Array<string>} 樣式類別列表
     */
    getClassNames() {
      const transformClassNames = ['transform-box']

      if (this.active) transformClassNames.push('active')
      if (this.isDragging) transformClassNames.push('transform-dragging')
      if (this.isResizing) transformClassNames.push('transform-resizing')
      if (this.isRotating) transformClassNames.push('transform-rotating')
      if (this.isReadyToDrag) transformClassNames.push('transform-ready-drag')
      if (this.isReadyToResize) transformClassNames.push('transform-ready-resize')
      if (this.isReadyToRotate) transformClassNames.push('transform-ready-rotate')
      return transformClassNames
    }
  },
  render() {
    return (
      <div
        ref="wrapper"
        style={this.style}
        class={this.getClassNames()}
        onTouchstart={this.handleMouseDown}
        onMousedown={this.handleMouseDown}>
        {this.renderContent ? this.renderContent(this) : this.$slots.default}
        {this.resizable1 && (
          <div class="resize-handler-wrapper">
            {this.resizeHandler.map((item) => {
              return (
                <span
                  data-resizetype={item}
                  key={item}
                  style={this.getNewHandler(item)}
                  class={`resize-handler ${item}`}
                />
              )
            })}
          </div>
        )}
        {this.rotatable1 && <span style={this.rotateHandler} data-type="rotate" class="rotate-handler" />}
      </div>
    )
  }
}
</script>

<style lang="less">
.transform-box {
  position: absolute;

  .resize-handler.tm {
    left: 50%;
  }

  .resize-handler.r {
    top: 50%;
  }

  .resize-handler.bm {
    left: 50%;
  }

  .resize-handler.l {
    top: 50%;
    cursor: pointer;
  }

  .resize-handler,
  .rotate-handler {
    display: none;
    position: absolute;
    border: 1px solid #607d8b;
    background: #fff;
    box-sizing: border-box;
    border-radius: 50%;
  }

  .rotate-handler {
    left: 50%;
    cursor: crosshair;
  }

  &.active {
    border: 1px dashed #607d8b;

    > .resize-handler-wrapper > .resize-handler,
    > .rotate-handler {
      display: inline-block;
    }
  }

  .br,
  .tr,
  .tl,
  .bl {
    background: #607d8b;
  }
}
</style>
